/**
 * Parser for the daily Chatcone audit report (the standalone HTML file).
 * The report embeds all its data as `const DATA = {...}` inside a <script> tag:
 *   DATA.findings    — the cases (id, page, sev, group, cat, issue, fix, admin, chat…)
 *   DATA.img         — { chatconeImageUrl: dataURL } for every screenshot
 *   DATA.adminVolume — { chatconeName: chats handled that day }
 * The owner reviews cases inside the HTML and saves a "แก้แล้ว" copy; that copy
 * adds per finding: verdict ('ok'|'no'|'worse'), verdictNote, scope
 * ('personal'|'team'|'none'), sendTo [names], perAdmin [{who, what, next}].
 * We pull the object out with brace matching (the HTML around it is irrelevant).
 *
 * The artifact report (7 ต.ค. onwards) is different: the cases sit as a plain
 * array in <script type="application/json" id="caseData">, the date is
 * `const DATE='20261007'`, screenshots are Chatcone links only (no DATA.img),
 * and the owner's verdicts stay in the page's own storage, not in the file.
 */

const CASE_DATA_RE = /<script type="application\/json" id="caseData">([\s\S]*?)<\/script>/;

/** Extract the embedded DATA object from the report HTML text. */
export function parseAuditReport(htmlText) {
  const marker = 'const DATA = ';
  const start = htmlText.indexOf(marker);
  if (start === -1) {
    const caseData = htmlText.match(CASE_DATA_RE);
    const dateKey = htmlText.match(/const DATE\s*=\s*'(\d{8})'/);
    if (!caseData) throw new Error('ไฟล์นี้ไม่ใช่รายงานตรวจแชท (ไม่พบข้อมูลรายงานข้างในไฟล์)');
    if (!dateKey) throw new Error('อ่านวันที่ของรายงานไม่ได้');
    const findings = JSON.parse(caseData[1]);
    return {
      reportDate: `${dateKey[1].slice(0, 4)}-${dateKey[1].slice(4, 6)}-${dateKey[1].slice(6, 8)}`,
      findings: Array.isArray(findings) ? findings : [],
      img: {},
      adminVolume: {},
      aliases: {},
      schemaVersion: 4,
      savedAt: null
    };
  }
  let i = start + marker.length;
  // The saved copy wraps the object in /*<<DATA*/ … /*DATA>>*/ markers
  while (i < htmlText.length && htmlText[i] !== '{') i++;
  let depth = 0;
  let inStr = false;
  let esc = false;
  const jsonStart = i;
  for (; i < htmlText.length; i++) {
    const c = htmlText[i];
    if (inStr) {
      if (esc) esc = false;
      else if (c === '\\') esc = true;
      else if (c === '"') inStr = false;
      continue;
    }
    if (c === '"') inStr = true;
    else if (c === '{') depth++;
    else if (c === '}') {
      depth--;
      if (depth === 0) break;
    }
  }
  const data = JSON.parse(htmlText.slice(jsonStart, i + 1));

  // Report date: prefer the DKEY constant ('20260911'), fall back to the Thai date text
  let reportDate = null;
  const dkey = htmlText.match(/const DKEY\s*=\s*'(\d{8})'/);
  if (dkey) {
    reportDate = `${dkey[1].slice(0, 4)}-${dkey[1].slice(4, 6)}-${dkey[1].slice(6, 8)}`;
  } else if (data.date) {
    reportDate = parseThaiDate(data.date);
  }
  if (!reportDate) throw new Error('อ่านวันที่ของรายงานไม่ได้');

  return {
    reportDate,
    findings: data.findings || [],
    img: data.img || {},
    adminVolume: data.adminVolume || {},
    // schema v2 (13 ก.ย. onwards): alias → canonical Chatcone name, e.g. 'Aom' → 'aomnaes.'
    aliases: data.adminAliases || {},
    schemaVersion: data.schema?.version || 1,
    savedAt: data.savedAt || null
  };
}

/** Resolve a report alias ('Ink', 'อ๋ง') to the canonical Chatcone name when the report lists one */
export function canonicalName(name, aliases = {}) {
  if (!name) return name;
  if (aliases[name]) return aliases[name];
  const norm = normalizeChatconeName(name);
  const hit = Object.keys(aliases).find((k) => normalizeChatconeName(k) === norm);
  return hit ? aliases[hit] : name;
}

const THAI_MONTHS = {
  'มกราคม': 1, 'กุมภาพันธ์': 2, 'มีนาคม': 3, 'เมษายน': 4, 'พฤษภาคม': 5, 'มิถุนายน': 6,
  'กรกฎาคม': 7, 'สิงหาคม': 8, 'กันยายน': 9, 'ตุลาคม': 10, 'พฤศจิกายน': 11, 'ธันวาคม': 12
};

/** '11 กันยายน 2569' (Buddhist year) → '2026-09-11' */
export function parseThaiDate(text) {
  const m = (text || '').match(/(\d{1,2})\s+(\S+)\s+(\d{4})/);
  if (!m) return null;
  const month = THAI_MONTHS[m[2]];
  if (!month) return null;
  const year = parseInt(m[3], 10) - 543;
  return `${year}-${String(month).padStart(2, '0')}-${String(m[1]).padStart(2, '0')}`;
}

/**
 * Normalize a Chatcone display name for matching:
 * the report is inconsistent about spaces/dots ('aomnaes.' vs 'aomnaes .',
 * 'PhatcharidaDachporn' vs 'Phatcharida Dachporn') and one name carries a
 * stray Thai phinthu character ('ฺBibie labu').
 */
export function normalizeChatconeName(name) {
  return (name || '')
    .normalize('NFC')
    .replace(/ฺ/g, '')      // stray Thai phinthu
    .replace(/[.\s]+/g, '')     // spaces and dots anywhere
    .trim()
    .toLowerCase();
}

/**
 * Phrases that mark a non-person entry in a finding's admin field
 * (system-level cases, team references, the owners). Substring match on the
 * normalized name — exact matching breaks on the report's spacing quirks.
 */
const NOT_A_PERSON_PATTERNS = [
  'ไม่ใช่ความผิด', 'ไม่มีใครตอบ', 'ทั้งสองเพจ', 'ช่วงไม่มีเวร',
  'ทีมคลัง', 'ทีมระบบ', 'satapon', 'polkrit',
  'พี่แก้ว' // answers chats but is not part of the admin team (owner's decision, 15 ก.ย.)
];

const isPersonName = (s) => {
  const n = normalizeChatconeName(s);
  if (n.length <= 1) return false;
  return !NOT_A_PERSON_PATTERNS.some((p) => n.includes(normalizeChatconeName(p)));
};

/** Split a finding's admin field ('aomnaes., Bite, Panda') into person names */
export function splitAdminNames(adminField) {
  return (adminField || '')
    .replace(/[()]/g, ',')
    .replace(/—/g, ',')
    .split(/[,+]/)
    .map((s) => s.trim())
    .filter(isPersonName);
}

const SEV_ORDER = ['low', 'med', 'high', 'critical'];
/** The owner ticked "หนักกว่านี้": one severity step up */
const bumpSeverity = (sev) => {
  const i = SEV_ORDER.indexOf(sev);
  return i === -1 || i === SEV_ORDER.length - 1 ? sev : SEV_ORDER[i + 1];
};

/** Report scope → the system's send mode */
export const SEND_MODE_FROM_SCOPE = { personal: 'person', team: 'all', none: 'none' };

/**
 * Turn one report finding into an editable import draft.
 *
 * people[] is the recipient checklist: everyone who appeared in the chat
 * (involvedNames) plus, in 'all' mode, every eligible admin. Names the report
 * blames (sendTo / perAdmin.who) get role 'fix' and their own what/next text;
 * everyone else is 'learn'. checked follows the send mode — see applySendMode.
 *
 * Findings the owner marked ไม่ใช่ปัญหา (verdict 'no') never reach here: the
 * import page drops them before calling this.
 */
export function findingToDraft(finding, imgMap, adminByNormName, eligibleAdmins, aliases = {}) {
  // Screenshots this case's chat actually references. A Chatcone link with no
  // embedded copy is kept too: the backend downloads it from Chatcone itself.
  const imageUrls = new Set();
  (finding.chat || []).forEach((entry) => (entry.i || []).forEach((u) => imageUrls.add(u)));
  const images = [...imageUrls]
    .filter((u) => imgMap[u] || /^https:\/\/s3\.chatcone\.net\//.test(u))
    .map((u) => ({ origUrl: u, dataUrl: imgMap[u] || null }));

  const canon = (n) => canonicalName(n, aliases);
  const involvedNames = splitAdminNames(finding.admin).map(canon);
  const blamedNames = (Array.isArray(finding.sendTo) ? finding.sendTo.filter(Boolean) : []).map(canon);
  const perAdmin = Array.isArray(finding.perAdmin) ? finding.perAdmin : [];
  const perAdminByNorm = {};
  perAdmin.forEach((p) => { if (p && p.who) perAdminByNorm[normalizeChatconeName(canon(p.who))] = p; });

  const sendMode = SEND_MODE_FROM_SCOPE[finding.scope] || 'person';

  // One entry per distinct Chatcone name in the chat / blamed list
  const byNorm = {};
  const order = [];
  const addName = (name) => {
    const norm = normalizeChatconeName(name);
    if (!norm || byNorm[norm]) return;
    const admin = adminByNormName[norm];
    const pa = perAdminByNorm[norm];
    // The v2 report says who is at fault (perAdmin.role); older reports imply it via sendTo
    const blamed = pa?.role ? pa.role === 'fix' : (blamedNames.some((b) => normalizeChatconeName(b) === norm) || !!pa);
    byNorm[norm] = {
      key: norm,
      chatconeName: name,
      adminId: admin?.id || null,
      nickname: admin?.nickname || name,
      role: blamed ? 'fix' : 'learn',
      what: pa?.what || '',
      next: pa?.next || '',
      inChat: true,
      checked: false
    };
    order.push(norm);
  };
  blamedNames.forEach(addName);
  involvedNames.forEach(addName);
  perAdmin.forEach((p) => p && p.who && addName(canon(p.who)));

  // v2 report: the hour's load per admin on shift, so a slow reply under a
  // flood is shown for what it is
  const load = finding.load && typeof finding.load === 'object' ? finding.load : null;
  const loadNote = load && load.highLoad
    ? `โหลดสูง ${load.chatsPerAdmin} แชท/คน ตอน ${String(load.hour).padStart(2, '0')}:00 (คนอยู่เวร ${load.adminsOnShift})`
    : '';

  const draft = {
    include: true,
    sourceId: finding.id,
    // schema v3: 'complaint' (ลูกค้าไม่พอใจ) or 'adlead' (ลูกค้าใหม่จากโฆษณา); older reports are all complaints
    section: finding.section === 'adlead' ? 'adlead' : 'complaint',
    page: finding.page === 'Tama' ? 'Tama' : 'Smile',
    sendMode,
    severity: finding.verdict === 'worse' ? bumpSeverity(finding.sev) : finding.sev,
    category: finding.group || 'อื่นๆ',
    title: finding.cat || '',
    customerName: finding.nick || finding.name || finding.cust || '',
    customerChannel: finding.ch || '',
    timeRange: finding.time || '',
    issue: finding.issue || '',
    fix: finding.fix || '',
    chatLog: finding.chat || [],
    images,
    reportVerdict: finding.verdict === 'ok' || finding.verdict === 'worse' ? finding.verdict : null,
    reportVerdictNote: finding.verdictNote || '',
    chatUrl: finding.chatUrl || '',
    loadNote,
    people: order.map((k) => byNorm[k]),
    expanded: false
  };
  return applySendMode(draft, sendMode, eligibleAdmins);
}

/**
 * Switch a draft's send mode and re-tick the checklist:
 *   person   → only the blamed people (role 'fix')
 *   involved → everyone who was in the chat
 *   all      → every eligible admin (people from the chat keep their roles)
 *   none     → nobody; the case goes to the back-office list
 */
export function applySendMode(draft, sendMode, eligibleAdmins = []) {
  let people = draft.people.filter((p) => p.inChat || p.manual);
  if (sendMode === 'all') {
    const known = new Set(people.filter((p) => p.adminId).map((p) => p.adminId));
    eligibleAdmins.forEach((a) => {
      if (known.has(a.id)) return;
      people.push({
        key: `admin-${a.id}`,
        chatconeName: null,
        adminId: a.id,
        nickname: a.nickname,
        role: 'learn',
        what: '',
        next: '',
        inChat: false,
        checked: true
      });
    });
  }
  people = people.map((p) => {
    let checked;
    if (sendMode === 'none') checked = false;
    else if (sendMode === 'person') checked = p.role === 'fix';
    else checked = true;
    return { ...p, checked };
  });
  return { ...draft, sendMode, people };
}

/**
 * Why this draft is not ready to send (empty = fine). Every one of these is
 * fixable on the import page itself: the report file is only a source of
 * cases, never a place a decision has to be made.
 */
export function draftWarnings(draft) {
  const warnings = [];
  if (draft.sendMode === 'none') return warnings;
  const checked = draft.people.filter((p) => p.checked);
  if (checked.some((p) => !p.adminId)) warnings.push('มีชื่อที่ยังไม่รู้ว่าเป็นใครในระบบ');
  if (checked.length === 0) warnings.push('ยังไม่ได้เลือกคนรับ');
  if (checked.some((p) => p.role === 'fix' && !p.what && !p.next)) warnings.push('คนที่ต้องปรับยังไม่มีข้อความรายคน');
  return warnings;
}

/** The import payload for one draft: only what the backend stores */
export function draftToPayload(draft) {
  const checked = draft.people.filter((p) => p.checked && p.adminId);
  return {
    page: draft.page,
    sourceId: draft.sourceId,
    sendMode: draft.sendMode,
    severity: draft.severity,
    category: draft.category,
    title: draft.title,
    issue: draft.issue,
    fix: draft.fix,
    timeRange: draft.timeRange,
    customerName: draft.customerName,
    customerChannel: draft.customerChannel,
    chatUrl: draft.chatUrl || null,
    loadNote: draft.loadNote || null,
    // `pin` only marks a comment still being typed on the import page
    chatLog: draft.chatLog.map(({ pin: _pin, ...entry }) => entry),
    images: draft.images,
    recipients: draft.sendMode === 'none' ? [] : checked.map((p) => ({
      adminId: p.adminId,
      role: p.role,
      what: p.what || null,
      next: p.next || null
    }))
  };
}

/**
 * The part of a chat log to show first: the case's time range plus a few
 * messages either side. Every bubble with a pinned comment (`note` / `better`
 * / `pin`, see ChatWithPins) is added on its own, so a far-off comment doesn't
 * stretch the window; ChatLogView marks the jump with a dated gap line.
 * Other entries with p=1 are the previous day's context and stay in the
 * full-day view only.
 */
export function buildExcerpt(chatLog, timeRange, pad = 5) {
  const log = Array.isArray(chatLog) ? chatLog : [];
  const today = log.map((e, i) => ({ e, i })).filter(({ e }) => !e.p);
  const m = (timeRange || '').match(/(\d{1,2}):(\d{2})\s*[-–]\s*(\d{1,2}):(\d{2})/);
  if (!m || today.length === 0) return { indexes: today.map(({ i }) => i), focused: false };
  const start = `${m[1].padStart(2, '0')}:${m[2]}`;
  const end = `${m[3].padStart(2, '0')}:${m[4]}`;
  if (start > end) return { indexes: today.map(({ i }) => i), focused: false };
  const hits = today.filter(({ e }) => e.t >= start && e.t <= end);
  if (hits.length === 0) return { indexes: today.map(({ i }) => i), focused: false };
  const first = today.findIndex(({ i }) => i === hits[0].i);
  const last = today.findIndex(({ i }) => i === hits[hits.length - 1].i);
  const from = Math.max(0, first - pad);
  const to = Math.min(today.length - 1, last + pad);
  const around = today.slice(from, to + 1).map(({ i }) => i);
  const pinned = log.map((e, i) => (e.note || e.better || e.pin ? i : -1)).filter((i) => i >= 0);
  return {
    indexes: [...new Set([...around, ...pinned])].sort((a, b) => a - b),
    focused: from > 0 || to < today.length - 1
  };
}
